import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccessToken } from '../../../../fetchData/useAccessToken'
import { useHandleError } from '../../../../fetchData/useHandleError'
import {
  makeResultFetch,
  FetchResponse,
} from '../../../../fetchData/genericFetch'
import { apiRequestParams } from '../../../../fetchData/fetchOptions'
import { ProjectForecastReportPeriod } from '../../../../interfaces/systemSettings/ProjectForecastReport'

const periodToInt: Record<ProjectForecastReportPeriod, number> = {
  P1: 0,
  P2: 1,
  P3: 2,
}

export const useCompleteProjectForecastReports = (
  year: number,
  period: ProjectForecastReportPeriod
) => {
  const getAccessToken = useAccessToken()
  const handleError = useHandleError()
  const queryClient = useQueryClient()

  const mutationFunction = useCallback(async () => {
    const accessToken = await getAccessToken()

    if (!accessToken) throw new Error('Access token is undefined')

    const response = await makeResultFetch<boolean>(
      `/reports/projectForecast/complete`,
      apiRequestParams(accessToken, 'POST', {
        year: year,
        period: periodToInt[period],
      })
    ).catch(() => {
      throw new Error(
        'Det gick inte att ansluta till servern. Kontrollera din internetuppkoppling.'
      )
    })

    if (!response.success) {
      throw new Error(response.message)
    }

    return response
  }, [getAccessToken, year, period])

  return useMutation<FetchResponse<boolean>, Error>({
    mutationFn: mutationFunction,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['projectForecastReports', year, period],
      })
    },
    onError: handleError,
  })
}
